import { useState } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { supabase } from '../supabaseClient';
import '../styles/DashboardTemplates.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function DashboardTemplates({ user, templates, setTemplates }) {
  const [templateName, setTemplateName] = useState('');
  const [templateText, setTemplateText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const handleCreateTemplate = async (e) => {
    e.preventDefault();

    if (!templateName.trim() || !templateText.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BACKEND_URL}/user-data/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: templateName.trim(),
          text: templateText.trim()
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to create template');
      }

      const data = await response.json();
      setTemplates(data.templates);
      setTemplateName('');
      setTemplateText('');
    } catch (err) {
      console.error('Error creating template:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BACKEND_URL}/user-data/templates`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ id: templateId })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to delete template');
      }

      const data = await response.json();
      setTemplates(data.templates);
    } catch (err) {
      console.error('Error deleting template:', err);
    }
  };

  const handleStartEdit = (template) => {
    setEditingId(template.id);
    setEditText(template.text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleSubmitEdit = async (template) => {
    if (!editText.trim()) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BACKEND_URL}/user-data/templates`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          id: template.id,
          name: template.name,
          text: editText.trim()
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Failed to update template');
      }

      const data = await response.json();
      setTemplates(data.templates);
      setEditingId(null);
      setEditText('');
    } catch (err) {
      console.error('Error updating template:', err);
    }
  };

  return (
    <div className="dashboard-templates">
      <h1 className="templates-title">Templates</h1>
      <p className="templates-subtitle">Create reusable templates for your searches</p>

      <form onSubmit={handleCreateTemplate} className="templates-form">
        <input
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Template name"
          className="templates-input"
          disabled={isSubmitting}
        />
        <textarea
          value={templateText}
          onChange={(e) => setTemplateText(e.target.value)}
          placeholder="e.g. Only include US-based founders. Founders must be from prestigious universities. Only include startups that haven't raised VC funding."
          className="templates-textarea"
          disabled={isSubmitting}
          rows={3}
        />
        <button
          type="submit"
          className="templates-create-button"
          disabled={isSubmitting || !templateName.trim() || !templateText.trim()}
        >
          {isSubmitting ? 'Creating...' : 'Create Template'}
        </button>
      </form>

      {templates && templates.length > 0 && (
        <div className="templates-list">
          {templates.map((template) => (
            <div key={template.id} className="template-item">
              <div className="template-header">
                <span className="template-name">{template.name}</span>
                <div className="template-actions">
                  <button
                    className="template-action-button"
                    onClick={() => handleStartEdit(template)}
                    title="Edit template"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="template-action-button delete"
                    onClick={() => handleDeleteTemplate(template.id)}
                    title="Delete template"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              {editingId === template.id ? (
                <div className="template-edit">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="templates-textarea"
                    rows={3}
                  />
                  <div className="template-edit-buttons">
                    <button
                      className="template-edit-cancel"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                    <button
                      className="template-edit-submit"
                      onClick={() => handleSubmitEdit(template)}
                      disabled={!editText.trim()}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              ) : (
                <p className="template-text">{template.text}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DashboardTemplates;
